/**
 * Camio API
 * @module api/camio
 */

import uuid from 'uuid';
import FetchUtils from '../util/fetchUtils';
import Settings from '../settings';

const API_VERSION = 'v1';

/**
 * @typedef {Object} AssetSearchOptions
 * @property {AssetSearchOptions} payload Payload object to send to the server
 * @property {string} payload.searchType Search type
 * @property {number} payload.activeView Active view (1 = MakeNew, 2 = Use Existing)
 * @property {string} payload.assetType Asset type (image/video/template)
 * @property {number} payload.categoryId Category Id
 * @property {number} payload.timeRange Time range (1 = All, 2 = Today, 3 = Yesterday)
 * @property {number} payload.contextId Context Id
 * @property {number} payload.pageNumber Which page of results to retrieve
 * @property {string} payload.searchTerms Search terms for asset request
 * @property {string} payload.filterBy Filter assets by "None" and "Favorites"
 * @property {string} payload.sortBy Sort by
 * @property {string} payload.sortDirection Sort Direction (Desc, Asc)
 * @property {number} payload.maxAssets Max number of assets to retrieve (Assets per page)
 */

/**
 * @typedef {Object} TemplateData
 * @property {string} slug Template slug
 * @property {string} inTime Timecode in
 * @property {string} outTime Timecode out
 * @property {string} channelId Channel id
 * @property {string} channelName Channel name
 */

/**
 * @typedef {Object} Replaceable
 * @property {string} id Replaceable id
 * @property {string} name Replaceable name
 * @property {string} type Replaceable type
 * @property {string} value Replaceable value
 * @property {string} provider Replaceable provider
 */

/**
 * @typedef {Object} MosObjectPayload
 * @property {string} mosObjectObjectId Mos object object id
 * @property {module:api/camio~Replaceable[]} replaceables Mos object replaceables
 * @property {module:api/camio~TemplateData} templateData Mos object template data
 * @property {string} templateId Mos object template id
 */

/**
 * @typedef {Object} PreviewPayload
 * @property {module:api/camio~Replaceable[]} replaceables Mos object replaceables
 * @property {string} mosObjectId Mos object id (Required only if its a mos object)
 * @property {string} assetId Asset id (Required only if it's not a mos object)
 */
const serverPath = Settings.serverPath;
const camioApiUrl = Settings.camioApiUrl;

/**
 * Handles the response returned by the camio api call.
 * @param  {Promise}  response The response returned by the camio api call
 */
const handleCamioResponse = response =>
  new Promise(resolve => {
    if (response.ok) {
      return resolve(response.json());
    }
    throw new Error('Response not ok.');
  }).then(data => {
    const res = typeof data === 'string' || data instanceof String ? JSON.parse(data) : data;
    if (res.statusCode && res.statusCode !== 200) {
      console.log(res);
      throw new Error(res.errorMessage || 'Unexpected error getting data from camio.');
    }
    return Promise.resolve(res);
  });

/**
 * Retrieves a list of assets from the server.
 * @func getAssets
 * @param {AssetSearchOptions} payload Search options to pass to the asset search handler.
 * @return {Promise<Object[]|Error>}
 */
export const getAssets = payload =>
  fetch(`${serverPath}AssetSearchHandler.ashx?type=${payload.searchType}`, {
    method: 'post',
    mode: 'cors',
    body: JSON.stringify({
      ...payload,
      requestId: uuid.v4()
    }),
    cache: 'no-cache',
    contentType: 'application/json',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getAssets failed: ${error}`);
    });

/**
 * Gets a list of contexts for specified user.
 * @func getContexts
 * @param  {number} userId Id of the user to retrieve contexts for.
 * @return {Promise<Object[]|Error>}
 */
export const getContexts = userId =>
  fetch(`${serverPath}Services/AssetRestService.svc/GetContexts/${encodeURIComponent(userId)}`, {
    method: 'get',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getContexts failed: ${error}`);
    });

/**
 * Gets a list of folders for a specified context.
 * @func getFolders
 * @param  {number} contextId Id of the context to retrieve folders for.
 * @return {Promise<Object[]|Error>}
 */
export const getFolders = contextId =>
  fetch(`${serverPath}Services/AssetRestService.svc/GetFolders/${encodeURIComponent(contextId)}`, {
    method: 'get',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getFolders failed: ${error}`);
    });

/**
 * Gets a list of categories for the specified asset type.
 * @func getCategories
 * @param  {string} assetType Asset type to get categories for.
 * @return {Promise<Object[]|Error>}
 */
export const getCategories = assetType =>
  fetch(
    `${serverPath}Services/AssetRestService.svc/GetAssetCategories/${encodeURIComponent(
      assetType
    )}`,
    {
      method: 'get',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      dataType: 'json'
    }
  )
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getCategories failed: ${error}`);
    });

export const getCategoryGroups = payload =>
  fetch(`${Settings.camioApiUrl}${API_VERSION}/category`, {
    mode: 'cors',
    method: 'post',
    dataType: 'json',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {})
  }).then(response => {
    if (response.ok) {
      return response.json();
    }
    return Promise.reject({ error: 'Error while querying category groups' });
  });

/**
 * Toggles the asset as being a favorite
 * @func toggleFavorite
 * @param  {string} id       Id of the asset
 * @param  {boolean} favorite Mark as favorite?
 * @return {Promise<boolean|Error>}
 */
export const toggleFavorite = (id, favorite) =>
  fetch(`${serverPath}UpdateFavoriteAssetsHandler.ashx?func=${favorite ? 'add' : 'remove'}`, {
    method: 'post',
    mode: 'cors',
    body: FetchUtils.createFormData({
      requestId: uuid.v4(),
      id
    }),
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`toggleFavorites failed: ${error}`);
    });

/**
 * Gets the template definition for the selected assets
 * @func getTemplateDefinition
 * @param  {string} id         Id of the asset you are getting the template definition for
 * @param  {string} searchType The search type ("id", "mos object")
 * @param  {number} activeView Active view (1 = MakeNew, 2 = Use Existing)
 * @return {Promise<string|Error>}
 */
export const getTemplateDefinition = (id, searchType, activeView) =>
  fetch(`${serverPath}AssetSearchHandler.ashx?type=${searchType}`, {
    method: 'post',
    mode: 'cors',
    body: JSON.stringify({
      requestId: uuid.v4(),
      activeView,
      assetType: 'template',
      assetId: id
    }),
    cache: 'no-cache',
    contentType: 'application/json',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .then(data => Promise.resolve(data))
    .catch(error => {
      throw new Error(`getTemplateDefinition failed: ${error}`);
    });

/**
 * Helper function for getTemplateDefinition
 * @func getMosObjectTemplate
 * @param  {string} id Mos object id
 * @return {Promise<string|Error>}
 */
export const getMosObjectTemplate = id =>
  getTemplateDefinition(id, 'mosobject', 2)
    .then(template => Promise.resolve(template))
    .catch(error => {
      throw new Error(`getMosObjectTemplate failed: ${error}`);
    });

/**
 * Gets a list of virtual channels.
 * @func getVirtualChannels
 * @return {Promise<Object[]|Error>}
 */
export const getVirtualChannels = () =>
  fetch(`${serverPath}Services/AssetRestService.svc/GetVirtualChannels`, {
    method: 'get',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getVirtualChannels failed: ${error}`);
    });

/**
 * Gets a list of virtual channels for an asset id.
 * @func getVirtualChannelsByAssetId
 * @return {Promise<Object[]|Error>}
 */
export const getVirtualChannelsByAssetId = assetId =>
  fetch(`${serverPath}Services/AssetRestService.svc/GetVirtualChannelsByAssetId/${assetId || ''}`, {
    method: 'get',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getVirtualChannelsByAssetId failed: ${error}`);
    });

/**
 * Gets a list of filter options.
 * @func getFilterOptions
 * @return {Promise<Object[]|Error>}
 */
export const getFilterOptions = (userId, assetType) =>
  fetch(
    `${serverPath}Services/AssetRestService.svc/GetFilters/${userId || ' '}/${assetType || ''}`,
    {
      method: 'get',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      dataType: 'json'
    }
  )
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getFilterByOptions failed: ${error}`);
    });

/**
 * Gets a list of automation transitions.
 * @func getAutomationTransitions
 * @return {Promise<Object[]|Error>}
 */
export const getAutomationTransitions = () =>
  fetch(`${serverPath}Services/AssetRestService.svc/GetAutomationTransitions`, {
    method: 'get',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getAutomationTransitions failed: ${error}`);
    });

/**
 * Generates a preview
 * @func generatePreview
 * @param  {module:api/camio~MosObjectPayload} payload Payload
 * @return {Promise<Object[]|Error>}
 */
export const generatePreview = payload =>
  fetch(`${serverPath}AssetPreviewHandler.ashx`, {
    method: 'post',
    mode: 'cors',
    body: JSON.stringify({
      requestId: 0,
      ...payload
    }),
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'json'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`generatePreview failed: ${error}`);
    });

/**
 * Inserts mos object to camio.
 * @func createMosObject
 * @param  {{module:api/camio~MosObjectPayload}} payload        Payload
 * @param  {boolean} insertExisting True if inserting an existing mos object.
 * @return {Promise<Object[]|Error>}
 */
export const createMosObject = (payload, createMethod, sendFromCamio) =>
  fetch(
    `${serverPath}MosObjectHandler.ashx?func=${createMethod}${
      sendFromCamio ? '&sendFromCamio=1' : ''
    }`,
    {
      method: 'post',
      mode: 'cors',
      body: JSON.stringify({
        requestId: uuid.v4(),
        ...payload
      }),
      cache: 'no-cache',
      credentials: 'include',
      dataType: 'text'
    }
  )
    .then(response => {
      if (response.ok) {
        return Promise.resolve(response.text());
      }
      throw new Error('Response not ok.');
    })
    .catch(error => {
      throw new Error(`createMosObject failed: ${error}`);
    });

export const deleteMosObject = payload =>
  fetch(`${serverPath}MosObjectHandler.ashx?func=delete`, {
    method: 'post',
    mode: 'cors',
    body: JSON.stringify({
      requestId: uuid.v4(),
      ...payload
    }),
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'text'
  })
    .then(response => {
      if (response.ok) {
        return Promise.resolve(response.text());
      }
      throw new Error('Response not ok');
    })
    .catch(error => {
      throw new Error(`deleteMosObject failed: ${error}`);
    });

export const getMosObjectTemplatePreviewPath = payload =>
  fetch(`${serverPath}MosObjectHandler.ashx?func=getpreviewpath`, {
    method: 'post',
    mode: 'cors',
    body: JSON.stringify({
      requestId: uuid.v4(),
      ...payload
    }),
    cache: 'no-cache',
    credentials: 'include',
    dataType: 'text'
  })
    .then(response => {
      if (response.ok) {
        return Promise.resolve(response.text());
      }
      throw new Error('Response not ok');
    })
    .catch(error => {
      throw new Error(`getMosObjectTemplateFilePath failed: ${error}`);
    });

export const createDeficientAsset = (contextId, assetType, title, author, externalId) =>
  fetch(
    `${serverPath}Services/AssetRestService.svc/CreateDeficientAsset/${encodeURIComponent(
      contextId
    )}/${encodeURIComponent(assetType)}/${encodeURIComponent(title)}/${encodeURIComponent(author)}${
      externalId ? `/${encodeURIComponent(externalId)}` : ''
    }`,
    {
      method: 'get',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      dataType: 'text'
    }
  )
    .then(response => {
      if (response.ok) {
        return response.text();
      }
      throw new Error('Response not ok');
    })
    .catch(error => {
      throw new Error(`createDeficientAsset failed: ${error}`);
    });

export const initialize = userId =>
  fetch(
    `${serverPath}Services/LuciBackendRestService.svc/initialize/${userId}?requestId=${uuid.v4()}`,
    {
      method: 'get',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include'
    }
  )
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`Initialization failed: ${error}`);
    });

export const getExternalConnectionStatus = () =>
  fetch(`${serverPath}Services/LuciBackendRestService.svc/connections`, {
    method: 'get',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getExternalConnectionStatus failed: ${error}`);
    });

export const getRenderMediaDestinations = () =>
  fetch(`${serverPath}Services/LuciBackendRestService.svc/rendermediadestinations`, {
    method: 'get',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include'
  })
    .then(handleCamioResponse)
    .catch(error => {
      throw new Error(`getRenderMediaDestinations failed: ${error}`);
    });

export const getMosObject = mosObjectId =>
  fetch(`${camioApiUrl}${API_VERSION}/mosobjects/${encodeURIComponent(mosObjectId)}`, {
    method: 'get',
    mode: 'cors',
    cache: 'no-cache'
  })
    .then(response => {
      if (response.ok) {
        return response.json();
      }
      throw new Error('Response not ok.');
    })
    .then(json => {
      if (json.stackTrace) {
        throw new Error(json);
      }
      return json;
    })
    .catch(error => {
      throw new Error(`getMosObject failed: ${error}`);
    });



// WEBPACK FOOTER //
// ./app/api/camio.js